// src/components/charts/mark-interaction.tsx
//
// ★★★ WHAT HAPPENS WHEN SOMEBODY POINTS AT, OR PRESSES, A MARK ON A CHART.
//
// ══ ⚠ WHAT WAS WRONG, from the owner's own screen ══
//
// The composition row drew a donut and then listed, underneath it, three text
// links: *"Open New bill bills"*, *"Open Upward adjustment bills"*, *"Open
// Downward adjustment bills"*. The chart was a picture and the links were the
// controls, side by side, saying the same three things twice.
//
// The owner's instruction is the obvious one and it is right: *"you just click
// on the chart, and on the New bill sector, if user select and click on the
// chart, it should bring user to the page that open new bill bills"* — and then,
// because a shape you can click has to tell you what it is first: *"when hover on
// top of the chart, it will have a note to state what is the sector … so user
// know what is it and what to select"*.
//
// ══ THE TWO HALVES, AND WHY NEITHER IS ENOUGH ALONE ══
//
//   * **A mark you can press must say what it is BEFORE you press it.** An angle
//     with no label is a guess; a reader should not have to click to find out
//     what they clicked. Hence the hover note, and it carries the label, the
//     value and its share — the same three facts the table view carries.
//   * **A mark that says what it is must be pressable.** Otherwise the reader
//     learns the name of a thing they then have to go and find in a list.
//
// ══ ⚠ WHY THIS IS NOT AN SVG `<a href>` ══
//
// An `<a>` inside SVG navigates by document load, which throws away the router
// and re-fetches the whole application. So a mark is a `path`/`rect` with
// `role="link"`, a keyboard handler and an `onSelect` the PAGE supplies — the
// page owns routing, the chart owns drawing. It is the same division
// `RankedBars.renderLink` already used, extended to the shapes themselves.
//
// ⚠ **KEYBOARD PARITY IS NOT OPTIONAL.** Enter and Space activate, exactly as
// they would on a link, and every mark takes focus in reading order. A
// drill-down reachable only by mouse is a drill-down half the requirement.
// `Space` has its default suppressed, or the page scrolls under the press.
//
// ⚠ **AND THERE IS EXACTLY ONE NOTE, WHICH IS THE SECOND THING THE OWNER
// REPORTED.** The first version drew this bubble AND left an SVG `<title>` on
// every mark, so pointing at a sector produced two overlapping labels — a
// four-line card of our own and the browser's own native tooltip underneath it.
// The `<title>` elements are gone from every interactive mark; `markProps`
// supplies an `aria-label` instead, which is what assistive technology reads and
// what the browser does NOT draw a second tooltip from.
//
// ⚠ **AND IT IS ONE LINE.** *"Show 'New bill 1315' will do, the display is too
// much information."* The share and the "Select to open these" prompt are gone:
// the share is in the table view where a reader who wants it will look, and a
// mark that lifts under the pointer and shows a cursor has already said it is
// pressable. A tooltip is a label, not a paragraph.
//
// ⚠ It also stays for as long as the pointer is on the mark — a native tooltip
// times out after a few seconds and vanishes while the reader is still looking
// at it, which is the other reason not to use one here.
import { useState, type KeyboardEvent, type ReactElement, type ReactNode } from 'react'

/**
 * What a hovered or focused mark says about itself — a name and a figure, and
 * deliberately nothing else.
 *
 * ⚠ A share, a date range and an invitation to click were all here and were all
 * removed: see this file's header. Anything a reader needs beyond the label and
 * the number is in the chart's table view, which every chart here has.
 */
export interface MarkNote {
  /** The mark's own name — "New bill", "AWAITING_L1", "29 Aug 2026". */
  readonly label: string
  /** The formatted figure, already localised by the caller. */
  readonly value: string
}

/**
 * The hover/focus note and the state that drives it.
 *
 * ⚠ ONE note per chart, not one per mark: two notes on screen at once is the
 * failure a per-mark popover produces when the pointer crosses a boundary.
 */
export function useMarkNote(): {
  readonly note: MarkNote | null
  readonly show: (note: MarkNote) => void
  readonly hide: () => void
} {
  const [note, setNote] = useState<MarkNote | null>(null)
  return { note, show: setNote, hide: () => setNote(null) }
}

/** The note itself, rendered over the plot. Nothing is drawn when nothing is pointed at. */
export function MarkNoteBubble({ note }: { readonly note: MarkNote | null }): ReactElement | null {
  if (note === null) return null
  return (
    <div className="pt-mark-note" role="status">
      <span className="pt-mark-note-label">{note.label}</span>
      <span className="pt-mark-note-value">{note.value}</span>
    </div>
  )
}

/**
 * The props that make one SVG shape behave like a link, or like a plain shape
 * when the caller supplied nothing to select.
 *
 * ⚠ Returns `{}` for a non-interactive mark rather than a disabled-looking one:
 * a shape with `role="link"` and no destination is worse than a shape with
 * neither, because assistive technology announces a link that goes nowhere.
 */
export function markProps(args: {
  readonly note: MarkNote
  readonly onSelect?: (() => void) | undefined
  readonly show: (note: MarkNote) => void
  readonly hide: () => void
  /**
   * ⚠ THE MARK'S OWN CLASSES, MERGED RATHER THAN REPLACED.
   *
   * The first version of this returned a bare `className: 'pt-mark-interactive'`,
   * and every caller spreads these props AFTER its own attributes — so it
   * silently overwrote `pt-series-fill-2 pt-donut-slice` and the donut lost its
   * colours. A helper that quietly drops the caller's styling is worse than one
   * that does not style at all.
   */
  readonly className?: string
}): Record<string, unknown> {
  const base = args.className ?? ''
  const hover = {
    ...(base.length > 0 ? { className: base } : {}),
    onMouseEnter: () => args.show(args.note),
    onMouseLeave: args.hide,
    onFocus: () => args.show(args.note),
    onBlur: args.hide,
  }

  if (args.onSelect === undefined) {
    /*
     * ⚠ A NON-INTERACTIVE MARK STILL CARRIES ITS NAME. It is not announced —
     * there is no role to announce it under — but the chart's own table view
     * carries every figure, which is what §5.3 rule 2 requires and what a reader
     * on assistive technology actually uses.
     */
    return hover
  }

  const activate = args.onSelect
  return {
    ...hover,
    className: `${base} pt-mark-interactive`.trim(),
    role: 'link',
    tabIndex: 0,
    /*
     * ⚠ THIS REPLACED A PER-MARK `<title>`, and that is why it is here rather
     * than on the shape. A `<title>` child makes the browser draw its OWN native
     * tooltip on hover, which appeared underneath the rendered note — two labels
     * for one mark. An `aria-label` says the same thing to a screen reader and
     * draws nothing.
     */
    'aria-label': `${args.note.label}: ${args.note.value}`,
    onClick: activate,
    onKeyDown: (event: KeyboardEvent<SVGElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      // ⚠ Space scrolls the page by default, which moves the chart out from
      // under the reader at the moment they act on it.
      event.preventDefault()
      activate()
    },
  }
}

/** The plot wrapper a note is positioned against. */
export function MarkNoteLayer({ children }: { readonly children: ReactNode }): ReactElement {
  return <div className="pt-mark-note-layer">{children}</div>
}
